import React, { useEffect, useState, Component } from 'react';
import { Menu, X, AlertCircle } from 'lucide-react';
import { ParentSidebarV2, ParentV2Route } from './ParentSidebarV2';
import { HomePage } from './pages/HomePage';
import { VoortgangPage } from './pages/VoortgangPage';
import { TipsPage } from './pages/TipsPage';
import { ActivityPage } from './pages/ActivityPage';
import { SettingsPage } from './pages/SettingsPage';
import { useAppStore } from '../../app/hooks/useAppStore';
import { BaseErrorBoundary } from '../../components/shared/BaseErrorBoundary';
import { SkeletonPage } from '../../components/shared/Skeleton';
import { DjayAvatar } from '../../mascot';
import { isParentAccount, type AccountsState, type Account, type LearnerAccount } from '../../accounts';
import { getLinkedLearners } from '../../accounts';
import { AppState } from '../../app/types';
interface ParentPageProps {
  accounts: AccountsState;
  activeAccount: Account;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  getLearnerData: (learnerId: string) => AppState | null;
  viewingLearnerId: string | null;
  setViewingLearner: (id: string | null) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
const PAGE_MAP: Record<ParentV2Route, ComponentType<Partial<ParentPageProps>>> = {
  home: HomePage,
  voortgang: VoortgangPage,
  tips: TipsPage,
  activiteit: ActivityPage,
  settings: SettingsPage as unknown as ComponentType<Partial<ParentPageProps>>
};
const ROUTE_LABELS: Record<ParentV2Route, string> = {
  home: 'Home',
  voortgang: 'Voortgang',
  tips: 'Tips',
  activiteit: 'Activiteit',
  settings: 'Instellingen'
};
interface ParentShellV2Props {
  onNavigateToLearner?: (learnerId: string) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
}
export function ParentShellV2({
  onNavigateToLearner,
  onLogout,
  onReturnHome
}: ParentShellV2Props) {
  const [activeRoute, setActiveRoute] = useState<ParentV2Route>('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    accounts,
    activeAccount,
    selectedGroup,
    getLearnerData,
    getLearnerModuleMastery,
    isInitialized,
    setViewingLearner,
    viewingLearnerId,
    linkParentToLearnerById
  } = useAppStore();
  const isParent = activeAccount && isParentAccount(activeAccount);
  const linkedLearners = isParent ? getLinkedLearners(accounts, activeAccount.id) : [];
  // Auto-select first child if only one is linked
  useEffect(() => {
    if (isParent && !viewingLearnerId && linkedLearners.length === 1) {
      setViewingLearner(linkedLearners[0].id);
    }
  }, [isParent, viewingLearnerId, linkedLearners.length, setViewingLearner]);
  if (!isInitialized) {
    return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-5263">
        <div className="hidden md:block w-64 bg-white border-r border-slate-200 shrink-0" data-id="element-5264" />
        <main className="flex-1 min-w-0 overflow-auto" data-id="element-5265">
          <SkeletonPage data-id="element-5266" />
        </main>
      </div>;
  }
  if (!isParent) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4" data-id="element-5267">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-sm border border-slate-200 text-center" data-id="element-5268">
          <h2 className="text-2xl font-black text-slate-900 mb-2" data-id="element-5269">
            Geen toegang
          </h2>
          <p className="text-slate-600 mb-8" data-id="element-5270">
            Je bent niet ingelogd als ouder. Gebruik de account switcher om naar
            een ouderaccount te wisselen.
          </p>
          {onReturnHome && <button onClick={onReturnHome} className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors shadow-sm" data-id="element-5271">
              Naar leerlingomgeving
            </button>}
        </div>
      </div>;
  }
  const selectedChild = viewingLearnerId ? linkedLearners.find(l => l.id === viewingLearnerId) : linkedLearners[0];
  const childData = selectedChild ? getLearnerData(selectedChild.id) : null;
  const PageComponent = PAGE_MAP[activeRoute] ?? HomePage;
  return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-5272">
      {/* Skip to content — a11y */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-emerald-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold" data-id="element-5273">
        Ga naar inhoud
      </a>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-slate-200 z-30 flex items-center justify-between px-4 shadow-sm" style={{
      paddingTop: 'max(0.875rem, env(safe-area-inset-top))'
    }} data-id="element-5274">
        <div className="font-bold text-slate-900 flex items-center gap-3 py-3" data-id="element-5275">
          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center border border-emerald-100 overflow-hidden" data-id="element-5276">
            <DjayAvatar size="xs" emotion="happy" data-id="element-5277" />
          </div>
          <div className="flex flex-col" data-id="element-5278">
            <span className="text-sm font-black leading-tight" data-id="element-5279">
              {ROUTE_LABELS[activeRoute]}
            </span>
            {selectedChild && <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider leading-tight" data-id="element-5280">
                {selectedChild.displayName}
              </span>}
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 -mr-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500" aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-5281">
          {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-5282" /> : <Menu className="w-6 h-6" data-id="element-5283" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-5284" />

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0 will-change-transform shadow-xl md:shadow-none
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-5285">
        <ParentSidebarV2 activeRoute={activeRoute} onNavigate={route => {
        setActiveRoute(route);
        setMobileMenuOpen(false);
      }} onLogout={onLogout} onReturnHome={onReturnHome} parentName={activeAccount.displayName} linkedLearners={linkedLearners} selectedChildId={viewingLearnerId} onSelectChild={setViewingLearner} data-id="element-5286" />
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 overflow-auto pt-16 md:pt-0 relative" data-id="element-5287">
        <BaseErrorBoundary logPrefix="Parent Portal" resetKey={activeRoute} fallback={({
        error,
        onReset
      }) => <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-id="element-5289">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4" data-id="element-5290">
                <AlertCircle className="w-8 h-8 text-emerald-600" data-id="element-5291" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2" data-id="element-5292">
                Er ging iets mis
              </h2>
              <p className="text-sm text-slate-500 mb-4 max-w-md" data-id="element-5293">
                {error.message}
              </p>
              <button onClick={onReset} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors" data-id="element-5294">
                Probeer opnieuw
              </button>
            </div>} data-id="element-5288">
          <PageComponent accounts={accounts} activeAccount={activeAccount} linkedLearners={linkedLearners} selectedChild={selectedChild} childData={childData} selectedGroup={selectedGroup.toString()} getLearnerModuleMastery={getLearnerModuleMastery} getLearnerData={getLearnerData} viewingLearnerId={viewingLearnerId} setViewingLearner={setViewingLearner} linkParentToLearnerById={linkParentToLearnerById} onNavigateToLearner={onNavigateToLearner} onNavigate={setActiveRoute} data-id="element-5295" />
        </BaseErrorBoundary>
      </main>
    </div>;
}